import { ReactElement, useCallback, useEffect, useRef, useState } from "react";
import 'chartjs-adapter-moment';
import zoomPlugin from 'chartjs-plugin-zoom'
import { Chart, ChartData, ChartOptions, TimeUnit } from "chart.js";
import { useDependency } from "../../../contexts/DependencyContext";
import { ChartService, ChartSize } from "../../../services/chart/ChartService";

export interface ILineChartSeries
{
    title: string,
    labels: string[],
    values: number[]
}

export const ChartPointsPerPixelRatio = 1;

export interface ILineChartProps
{
    size: ChartSize,
    series: ILineChartSeries[],
    enableZoom: boolean,
    displayLegend: boolean,
    xLabel?: string,
    yLabel?: string,
    timeUnit: TimeUnit,
    getTooltipLabel?: (context: any) => string | string[]
    getTooltipTitle?: (tooltipItems: any[]) => string
}

const LineChart = (props: ILineChartProps): ReactElement =>
{
    const service = useDependency(ChartService);
    const [lastProps, setLastProps] = useState<ILineChartProps | null>(null);
    const lineChart = useRef<ReactElement>(<></>);

    const havePropsChanged = useCallback((): boolean => {
        let propsHaveChanged = false;
        if (!lastProps) propsHaveChanged = true;
        if (!propsHaveChanged && lastProps) propsHaveChanged = Object.keys(props).length !== Object.keys(lastProps).length;

        if (!propsHaveChanged && lastProps) {
            for (let i = 0; i < Object.values(props).length; i++) {
                const propVal = Object.values(props)[i];
                const lastPropVal = Object.values(lastProps)[i];
                propsHaveChanged = propVal !== lastPropVal;

                if (propsHaveChanged) {
                    break
                };
            }
        }
        return propsHaveChanged;
    }, [props, lastProps])

    function getData(series: ILineChartSeries[]): ChartData {
        return {
            labels: series.flatMap(x => x.labels).filter((value, index, self) => self.indexOf(value) === index),
            datasets: series.map((x, i) =>
            {
                const color = i === 0 ? 'rgba(35,122,235,0.6)' : 'rgba(235,122,35,0.6)';

                return {
                    label: x.title,
                    data: x.values,
                    fill: true,
                    backgroundColor: color,
                    borderColor: color,
                    pointBackgroundColor: color,
                    borderWidth: 1,
                    pointBorderWidth: 0.5,
                    pointRadius: 0.5,
                    tension: 0.5
                }
            })
        }
    }

    const getOptions = useCallback((options: ILineChartProps): ChartOptions =>
    {
        const chartOptions = JSON.parse(JSON.stringify(service.defaultOptions));

        const { timeUnit, xLabel, yLabel, displayLegend, getTooltipLabel, getTooltipTitle, enableZoom } = options;

        if (chartOptions?.scales)
        {
            if (chartOptions.scales.x && timeUnit)
            {
                chartOptions.scales.x.time = { unit: timeUnit };
                if (timeUnit === "day") chartOptions.scales.x.time.displayFormats = { 'day': 'MMM DD' };
            }
            if (xLabel && chartOptions.scales.x && chartOptions.scales.x.title)
            {
                chartOptions.scales.x.title.text = xLabel
            };

            if (yLabel)
            {
                if (chartOptions.scales.y.title) chartOptions.scales.y.title.text = yLabel;
            }
        }

        if (chartOptions?.plugins?.legend)
            chartOptions.plugins.legend.display = displayLegend;

        if (getTooltipLabel && chartOptions.plugins?.tooltip?.callbacks)
        {
            chartOptions.plugins.tooltip.callbacks.label = getTooltipLabel;
        }

        if (getTooltipTitle && chartOptions.plugins?.tooltip?.callbacks)
            chartOptions.plugins.tooltip.callbacks.title = getTooltipTitle;

        if (enableZoom)
        {
            Chart.register(zoomPlugin);
            chartOptions.plugins.zoom =
            {
                pan: {
                    enabled: true,
                    mode: "xy",
                },
                zoom: {
                    mode: "xy",
                    wheel: {
                        enabled: true
                    },
                    pinch: {
                        enabled: true
                    },
                    onZoomStart({ chart }: { chart: Chart })
                    {
                        const zoom = chart?.config?.options?.plugins?.zoom?.zoom ? chart.config.options.plugins.zoom.zoom : null;
                        if (zoom && zoom.mode)
                        {
                            zoom.mode = service.getZoom();
                        };
                    },
                },
            }
        }

        return chartOptions;
    }, [service])

    function getHeight(size: ChartSize): number
    {
        switch (size) {
            case 'large':
                return 500;

            case 'small':
                return 300;

            default:
                return 400;
        }
    }

    useEffect(() => {
        if (havePropsChanged()) {
            lineChart.current = service.setChart(
                getData(props.series),
                getOptions(props),
                getHeight(props.size)
            );
            const shouldSetEventListener = true;
            if (shouldSetEventListener) service.setEventListeners();
            setLastProps(props);
        }
    }, [service, havePropsChanged,getOptions, props])

    return (
        <>
            {lineChart.current}
        </>
    )
}

export default LineChart;